import { z } from 'zod'

const Vector3TupleSchema = z.tuple([z.number(), z.number(), z.number()])
const QuaternionTupleSchema = z.tuple([z.number(), z.number(), z.number(), z.number()])

export const ConnectorDefinitionSchema = z.object({
  id: z.string().min(1),
  type: z.enum([
    'g-cube-face',
    'cube-slot',
    'cross-female',
    'cross-male',
    'motor-female',
    'motor-male',
    'link-end',
    'axle',
    'wheel-hub',
  ]),
  position: Vector3TupleSchema,
  normal: Vector3TupleSchema,
  up: Vector3TupleSchema,
  allowedTypes: z.array(z.string().min(1)),
  rotationSteps: z.number().int().min(1).max(24),
})

export const PartDefinitionSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string().min(1),
  subtitle: z.string().min(1),
  category: z.enum(['g-cube', 'link', 'frame', 'wheel', 'sensor', 'accessory']),
  series: z.string().optional(),
  kind: z.enum(['motor', 'sensor', 'link', 'battery', 'wheel', 'frame']),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  modelUrl: z.string().startsWith('/').nullable(),
  modelUrls: z.array(z.string()).optional(),
  modelTransforms: z.array(z.object({
    position: Vector3TupleSchema,
    rotationQuaternion: QuaternionTupleSchema,
  })).optional(),
  modelUnit: z.enum(['meter', 'millimeter']).optional(),
  modelUpAxis: z.enum(['y', 'z']).optional(),
  thumbnailUrl: z.string().startsWith('/').nullable(),
  dimensions: Vector3TupleSchema,
  physics: z.object({
    mass: z.number().positive(),
    friction: z.number().min(0),
    restitution: z.number().min(0).max(1),
    collider: z.object({
      type: z.enum(['cuboid', 'ball', 'cylinder']),
      size: Vector3TupleSchema,
    }),
  }),
  connectors: z.array(ConnectorDefinitionSchema),
  specStatus: z.enum(['verified', 'estimated', 'source-only']).default('estimated'),
  sourceOrder: z.number().int().positive().optional(),
  source: z.object({
    family: z.string(),
    code: z.string(),
    stlFiles: z.array(z.string()),
    stepFiles: z.array(z.string()),
  }).optional(),
  motor: z.object({
    maxRpm: z.number().positive(),
    maxTorque: z.number().positive(),
    gearRatio: z.number().positive(),
  }).optional(),
}).superRefine((part, context) => {
  if (part.modelTransforms && part.modelTransforms.length !== part.modelUrls?.length) {
    context.addIssue({
      code: 'custom',
      path: ['modelTransforms'],
      message: 'modelTransforms must match modelUrls one-to-one',
    })
  }
})

export const PartCatalogSchema = z.object({
  version: z.literal(1),
  parts: z.array(PartDefinitionSchema).min(1),
}).superRefine((catalog, context) => {
  const ids = new Set<string>()
  catalog.parts.forEach((part, index) => {
    if (ids.has(part.id)) {
      context.addIssue({ code: 'custom', path: ['parts', index, 'id'], message: `Duplicate part id: ${part.id}` })
    }
    ids.add(part.id)
  })
})

export type PartDefinition = z.infer<typeof PartDefinitionSchema>
export type ConnectorDefinition = z.infer<typeof ConnectorDefinitionSchema>
